// © Microsoft Corporation. All rights reserved.

import { PageMeasure, PerfTimeline } from "./PerfTimeline";
import { canUseDOM, getLocationHref } from "@msnews/isomorphic-util";

import { logger } from "../logging";

/**
 * In case we're tracking only specific experience types, this will contain
 * the keys of experiences that were requested to be tracked.
 */
let experiencesForPerf: { [key: string]: boolean };

/**
 * In case we're excluding specific experiences from tracking. Useful when
 * we want to track all experiences, except for a few noisy ones, for example.
 */
let experiencesExcludedForPerf: { [key: string]: boolean };

/**
 * Flag to indicate we want to log marks & measurements against the performance API.
 * By default, all marks and measurements will only happen against an in-memory object.
 * Note that when you enable this option, it will incur an additional overhead from using
 * the API. You should only use this option when you want to look at the perf trace in
 * the browser's F12 tools. This is not required otherwise.
 *
 * Controlled via the experPerfMark=true QSP.
 */
let addPerformanceMarks: boolean;

/**
 * Flag to add any perf timeline measurements to the global _pageTimings object
 */
let addPageTimings: boolean;

/**
 * Flag to indicate that we're tracking all experiences.
 *
 * Controlled via the expPerf=all QSP
 */
let trackAllExp: boolean;

/**
 * Top-level flag to enable perf timeline features
 */
let enablePerfTimeline: boolean;

/**
 * Dictionary containing the unique {PerfTimeline} object instances across the app.
 * When related measurements on the same perf timeline is required across unrelated parts
 * of the code, this will guarantee you're tracking those measurements under the same
 * perf timeline using a combination of the experience name and instance id.
 */
let perfTimelineInstances: { [instanceId: string]: PerfTimeline };

/**
 * Initializes experience perf diagnostic specific state
 * Note: this method should not be called explicitly (other than from UTs.)
 *      It is already called by default at the beginning of module initialization.
 * @param locationHref - the value of window.location.href from which to parse options
 */
export function initExperienceDiagnostics(locationHref: string): void {

    addPageTimings = false;
    addPerformanceMarks = false;
    experiencesExcludedForPerf = {};
    experiencesForPerf = {};
    perfTimelineInstances = {};
    trackAllExp = false;

    try {
        const searchParams = (new URL(locationHref)).searchParams;
        const expPerfQsp = searchParams.get("expPerf");
        enablePerfTimeline = !!expPerfQsp;

        // if we weren't requested for any experience perf tracking, return early.
        if (!enablePerfTimeline) {
            return;
        }

        trackAllExp = expPerfQsp === "all";

        // if an explicit experience name list was specified, convert it to an boolean map
        // for efficient lookup so that we can create or avoid cration of the timeline object
        // for whatever experience needs instrumentation.
        if (!trackAllExp && expPerfQsp) {
            expPerfQsp.split(",").forEach(expName =>
                experiencesForPerf[expName] = true
            );
        }

        // parse explicit exclusions from perf timeline
        const expPerfSkipQsp = searchParams.get("expPerfSkip");
        if (expPerfSkipQsp) {
            expPerfSkipQsp.split(",").forEach(expName =>
                experiencesExcludedForPerf[expName] = true
            );
        }

        addPerformanceMarks = searchParams.get("expPerfMarks") === "true";
        addPageTimings = searchParams.get("expPerfTimings") === "true";

    } catch (e) {
        logger.logError("Error parsing URL for detecting experience diagnostic switches: " + e);
    }
}

// initialize right away
// TODO (http://osgvsowi/24596752): This needs reinitialized on each request when server side rendering
if (canUseDOM()) {
    initExperienceDiagnostics(getLocationHref());
}

/**
 * Gets or creates a perf timeline object for the given experience instance
 * @param experienceName - the name of the experience for which to create the timeline
 * @param instanceId - the ID that will uniquely identify this timeline across multiple instances.
 * @param async - Optional flag to indicate the default tracing mode for this timeline.
 *  Specifying true will render the measures under its own process id in the app trace.
 *  Useful if the timeline will contain many async operations that frequently overlap with each other.
 *  Note that this value can be overriden when taking an individual measure as well.
 */
export function tryGetPerfTimeline(experienceName: string, instanceId: string, async?: boolean): PerfTimeline {

    if (!shouldTrackPerfTimeline(experienceName)) {
        return;
    }

    const instanceKey = getInstanceKeyCore(experienceName, instanceId);

    const perfTimeline = perfTimelineInstances[instanceKey];
    if (perfTimeline) {
        return perfTimeline;
    }

    return perfTimelineInstances[instanceKey] = PerfTimeline.create(
        experienceName,
        instanceId,
        addPerformanceMarks,
        addPageTimings,
        async
    );
}

/**
 * Renames the given perf timeline object and moves the lookup dictionary reference under the new key
 * @param perfTimeline - the perf timeline to rename
 * @param newExperienceName - the new name for the perf timeline
 */
export function renamePerfTimeline(perfTimeline: PerfTimeline, newExperienceName: string): void {

    const oldInstanceKey = getInstanceKey(perfTimeline);

    if (perfTimelineInstances[oldInstanceKey] !== perfTimeline) {
        logger.logError(`Attempted to rename a perf timeline object with mismatching instance key ${oldInstanceKey}`);
        return;
    }

    // rename the perf timeline to the new name
    perfTimeline.rename(newExperienceName);

    // delete the old reference
    delete perfTimelineInstances[oldInstanceKey];

    // ...and add the new instance under the new key
    const newInstanceKey = getInstanceKey(perfTimeline);
    perfTimelineInstances[newInstanceKey] = perfTimeline;
}

/**
 * Returns the list of page measures from all the perf timeline objects being tracked
 */
export function getExperienceMeasures(): Array<PageMeasure> {
    const pageMeasures: Array<PageMeasure> = [];
    Object.keys(perfTimelineInstances).forEach(instanceKey =>
        pageMeasures.push(...perfTimelineInstances[instanceKey].getPageMeasures())
    );
    return pageMeasures;
}

/**
 * Helper to check whether we should track perf timeline for the given experience
 * @param experienceName - the name of the experience to check
 * @returns {boolean} indicating whether the given experience should be tracked for perf
 */
function shouldTrackPerfTimeline(experienceName: string): boolean {
    return enablePerfTimeline
        && !experiencesExcludedForPerf[experienceName]
        && (trackAllExp || experiencesForPerf[experienceName]);
}

/**
 * Gets the instance key to store the experience timeline
 * @param perfTimeline - the timeline object from which to get the instance key.
 * @returns the cache key that represents the location to which to store the perf timeline.
 */
function getInstanceKey(perfTimeline: PerfTimeline): string {
    const { timelineName, instanceId } = perfTimeline;
    return getInstanceKeyCore(timelineName, instanceId);
}

/**
 * Gets the instance key to store the experience timeline
 * @param experienceName - the name of the experience
 * @param instanceId - the instance ID for the perf timeline.
 * @returns the cache key that represents the location to which to store the perf timeline.
 */
function getInstanceKeyCore(experienceName: string, instanceId: string): string {
    return `${experienceName}-${instanceId || "0"}`;
}